// The thin bottom strip: compile status + gate verdict on the left; diagnostic
// counts, active language, target network, and the funding address on the right.
// Parameterless, binds to the store.

import { useState } from "react";
import { Loader2, Check, X, Circle, CircleAlert, TriangleAlert, Copy } from "lucide-react";
import { useIde } from "../store";
import type { CompileStatus } from "../store";
import "./shell.css";

import { networkDef } from "../network";

/** Where vim mode renders its mode indicator and `:` command line. The editor
 *  owns vim itself; this is only the place it draws, because a vim status line
 *  belongs in the status bar rather than floating over the code. */
export const VIM_STATUS_ID = "vim-status";

interface StatusFace {
  cls: string;
  label: string;
  glyph: "spinner" | "check" | "cross" | "dot";
}

function faceFor(status: CompileStatus): StatusFace {
  switch (status) {
    case "linting":
      return { cls: "sb-status--busy", label: "Linting…", glyph: "spinner" };
    case "compiling":
      return { cls: "sb-status--busy", label: "Compiling…", glyph: "spinner" };
    case "ok":
      return { cls: "sb-status--ok", label: "Compiled", glyph: "check" };
    case "error":
      return { cls: "sb-status--error", label: "Error", glyph: "cross" };
    case "idle":
    default:
      return { cls: "sb-status--idle", label: "Ready", glyph: "dot" };
  }
}

function StatusGlyph({ glyph }: { glyph: StatusFace["glyph"] }) {
  switch (glyph) {
    case "spinner":
      return <Loader2 size={11} className="spin" />;
    case "check":
      return <Check size={12} strokeWidth={2.5} />;
    case "cross":
      return <X size={12} strokeWidth={2.5} />;
    case "dot":
    default:
      return <Circle size={8} fill="currentColor" strokeWidth={0} />;
  }
}

function truncateAddress(addr: string): string {
  if (addr.length <= 18) return addr;
  return `${addr.slice(0, 8)}…${addr.slice(-6)}`;
}

export function StatusBar() {
  const status = useIde((s) => s.status);
  const result = useIde((s) => s.result);
  const diagnostics = useIde((s) => s.diagnostics);
  const activePath = useIde((s) => s.activePath);
  const files = useIde((s) => s.files);
  const network = useIde((s) => s.prefs.network);
  const setBottomTab = useIde((s) => s.setBottomTab);

  const [copied, setCopied] = useState(false);

  const face = faceFor(status);
  const errors = diagnostics.filter((d) => d.severity === "error").length;
  const warnings = diagnostics.filter((d) => d.severity === "warning").length;

  const activeFile = activePath ? files[activePath] : undefined;
  const langLabel =
    activeFile?.language === "seal"
      ? "Seal"
      : activeFile?.language === "json"
        ? "JSON"
        : activeFile
          ? "Plain Text"
          : null;

  const gate = result?.gate;
  const address = result?.address;

  async function copyAddress() {
    if (!address) return;
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      /* clipboard unavailable, non-fatal */
    }
  }

  function gateChip() {
    if (!gate) return null;
    if (gate.divergence) {
      return (
        <span className="sb-gate sb-gate--divergence" title="A spend path diverged from its spec">
          <span className="sb-gate-dot" />
          Divergence
        </span>
      );
    }
    if (gate.mayProceed && result?.ok !== true) {
      return (
        <span
          className="sb-gate sb-gate--unproven"
          title="Every spend path is proven, but the contract could not be assembled"
        >
          <span className="sb-gate-dot" />
          Not assembled
        </span>
      );
    }
    if (gate.mayProceed) {
      return (
        <span className="sb-gate sb-gate--proven" title="Every spend path proven, safe to fund">
          <span className="sb-gate-dot" />
          Proven
        </span>
      );
    }
    const n = gate.unproven;
    return (
      <span
        className="sb-gate sb-gate--unproven"
        title={`${n} unproven ${n === 1 ? "path" : "paths"}, funding refused`}
      >
        <span className="sb-gate-dot" />
        {n} unproven
      </span>
    );
  }

  return (
    <footer className="sb" role="contentinfo">
      <div className="sb-left">
        <span className={`sb-status ${face.cls}`} title="Compiler status">
          <StatusGlyph glyph={face.glyph} />
          <span>{face.label}</span>
        </span>
        {gateChip()}
        <span id={VIM_STATUS_ID} className="sb-vim" />
      </div>

      <div className="sb-right">
        <button
          type="button"
          className="sb-item sb-counts"
          onClick={() => setBottomTab("problems")}
          title="View problems"
          aria-label={`${errors} errors, ${warnings} warnings`}
        >
          <span className={`sb-count${errors > 0 ? " sb-count--error" : ""}`}>
            <CircleAlert size={12} strokeWidth={2} />
            {errors}
          </span>
          <span className={`sb-count${warnings > 0 ? " sb-count--warning" : ""}`}>
            <TriangleAlert size={12} strokeWidth={2} />
            {warnings}
          </span>
        </button>

        {langLabel && <span className="sb-item sb-readonly">{langLabel}</span>}

        <span
          className="sb-item sb-readonly"
          title={`Address network: ${networkDef(network).label} (${networkDef(network).prefix}). This build cannot produce a mainnet address.`}
        >
          {networkDef(network).label.toLowerCase()}
        </span>

        {address && (
          <button
            type="button"
            className={`sb-item sb-address${copied ? " sb-address--copied" : ""}`}
            onClick={copyAddress}
            title={copied ? "Copied!" : `${address}\nClick to copy`}
            aria-label="Copy funding address"
          >
            {copied ? <Check size={11} strokeWidth={2.5} /> : <Copy size={11} strokeWidth={2} />}
            <span className="sb-address-text">{copied ? "Copied" : truncateAddress(address)}</span>
          </button>
        )}
      </div>
    </footer>
  );
}
